import React, { Component } from 'react';
import { hashHistory } from 'react-router';
import MemberListScreen from './Components/MemberListScreen';
import EditMemberModal from './Components/EditMemberModal';
import SummaryScreen from './Components/SummaryScreen';
import AmendmentProgressDialog from '../shared/Components/AmendmentProgressDialog';
import { extractMembersFromPolicy } from '../ChangeCoverage/utils/coverageHelpers';
import { buildMemberUpdateQuotation } from './utils/quotationBuilder';
import { processMemberDetailsAmendment } from './utils/amendmentApi';

class ModifyMemberDetailsScreen extends Component {
  constructor(props) {
    super(props);

    const { policy } = props;
    const allMembers = extractMembersFromPolicy(policy);

    this.state = {
      allMembers,
      selectedMember: null,
      updatedMember: null,
      changes: [],
      showEditModal: false,
      showSummary: false,
      // Amendment progress tracking
      steps: { quote: 'pending', swap: 'pending', note: 'pending' },
      quoteError: null,
      swapError: null,
      noteError: null,
      showSuccessDialog: false
    };
  }

  // Handle edit button click
  handleEditMember = (member) => {
    this.setState({
      selectedMember: member,
      showEditModal: true
    });
  };

  // Handle close edit modal
  handleCloseModal = () => {
    this.setState({
      selectedMember: null,
      showEditModal: false
    });
  };

  // Handle save changes from modal - go to summary
  handleSaveChanges = (updatedMember, changes) => {
    this.setState({
      updatedMember,
      changes,
      showEditModal: false,
      showSummary: true
    });
  };

  // Handle back from summary
  handleBackFromSummary = () => {
    this.setState({
      showSummary: false,
      showEditModal: true
    });
  };

  // Handle confirm from summary - process amendment
  handleConfirmChanges = async () => {
    const { policy } = this.props;
    const { updatedMember, changes } = this.state;

    // Reset step statuses
    this.setState({
      steps: { quote: 'pending', swap: 'pending', note: 'pending' },
      quoteError: null,
      swapError: null,
      noteError: null,
      showSuccessDialog: false
    });

    try {
      // Build quotation payload
      const quotationPayload = buildMemberUpdateQuotation(policy, updatedMember);

      // Process amendment with step tracking
      const updateStatus = (step, status) => {
        // Update step status (loading, success, error)
        this.setState(prevState => ({
          steps: { ...prevState.steps, [step]: status }
        }));
      };

      const result = await processMemberDetailsAmendment(
        quotationPayload,
        policy,
        updatedMember,
        changes,
        updateStatus
      );

      // Amendment succeeded (even if note failed)
      this.setState({
        showSuccessDialog: true,
        noteError: result.noteError || null
      });
    } catch (error) {
      console.error('Failed to update member details:', error);
      const errorMessage = error.message || 'Failed to update member details. Please try again.';

      // Determine which step failed
      this.setState(prevState => {
        const quoteError = prevState.steps.quote === 'error' ? errorMessage : null;
        const swapError = prevState.steps.swap === 'error' ? errorMessage : null;

        return { quoteError, swapError };
      });
    }
  };

  // Handle success - redirect to policy page
  handleSuccess = () => {
    const { policy } = this.props;
    this.setState({ showSuccessDialog: false });
    // Navigate to policy processing page
    hashHistory.push(`admin/policy-processing/${policy.policy_number}`);
  };

  // Handle retry after error
  handleRetry = () => {
    this.setState({
      steps: { quote: 'pending', swap: 'pending', note: 'pending' },
      quoteError: null,
      swapError: null,
      noteError: null
    });
  };

  // Handle cancel
  handleCancel = () => {
    if (this.props.onBack) {
      this.props.onBack();
    }
  };

  render() {
    const { policy } = this.props;
    const {
      allMembers,
      selectedMember,
      updatedMember,
      changes,
      showEditModal,
      showSummary,
      steps,
      quoteError,
      swapError,
      noteError
    } = this.state;

    // Derive processing state
    const isProcessing = steps.quote === 'loading' || steps.swap === 'loading' || steps.note === 'loading';

    // Show summary screen
    if (showSummary) {
      return (
        <div>
          <SummaryScreen
            member={updatedMember}
            changes={changes}
            policy={policy}
            onBack={this.handleBackFromSummary}
            onConfirm={this.handleConfirmChanges}
          />

          <AmendmentProgressDialog
            steps={steps}
            quoteError={quoteError}
            swapError={swapError}
            noteError={noteError}
            successMessage="Member details have been successfully updated. The policy has been amended."
            onViewPolicy={this.handleSuccess}
            onRetry={this.handleRetry}
          />
        </div>
      );
    }

    return (
      <div>
        {/* Member List Screen */}
        <MemberListScreen
          members={allMembers}
          policy={policy}
          onEditMember={this.handleEditMember}
          onCancel={this.handleCancel}
        />

        {/* Edit Member Modal */}
        <EditMemberModal
          show={showEditModal}
          member={selectedMember}
          onSave={this.handleSaveChanges}
          onClose={this.handleCloseModal}
          isProcessing={isProcessing}
        />
      </div>
    );
  }
}

export default ModifyMemberDetailsScreen;
